/**
 * The Editor half of a spritesheet import (spec 034): reading a dropped
 * `.zip` or `.png` into rows the dialog shows (`readSheetExport`), and
 * storing the confirmed ones (`commitSheetImport`): upload each sheet into the
 * sprite's media folder, set its cut, then change the scene in one undo step.
 * Nothing is stored before the author confirms (D-1); a failure part-way
 * deletes what this import created.
 */

import JSZip from 'jszip';
import type { SheetCut, SheetRect, SpriteSize } from '../../../Shared/src/@types';
import {
  IMPORT_MAX_BYTES, SheetImportError, checkExportLimits, checkMetaSize, checkSheetSize, classifyExport, cutFromGrid, cutFromJson,
  parseSheetJson, pngSize, type ExportAnimation, type ExportEntry,
} from '../../../Shared/src/sheets/sheetImport';
import { frameDurations, sheetFrameRect } from '../../../Shared/src/utils/spriteSheet';
import { packFrames, packLayout, packedCut } from './packFrames';
import type { apiClient as ApiClientInstance } from '../api/client';
import { useMediaSheetStore } from '../stores/mediaSheetStore';
import { useSceneStore, type SheetImportItem, type SpriteStateError } from '../stores/sceneStore';

type Client = Pick<typeof ApiClientInstance, 'getMediaFolders' | 'createMediaFolder' | 'deleteMediaFolder' | 'uploadMedia' | 'updateMedia' | 'deleteMedia'>;

/** One animation the dialog can import. */
export interface ImportRow {
  /** Stable key (the first path it came from). */
  id: string;
  name: string;
  kind: ExportAnimation['kind'];
  cut: SheetCut;
  /** The sheet's natural size (a packed sheet's size for numbered frames). */
  size: SpriteSize;
  /** Frame 0's rect in the sheet. */
  firstFrame: SheetRect;
  durationMs: number;
  /** The first frame's still: the sheet (show `firstFrame` of it), or for frames the first frame's own PNG. */
  still: { blob: Blob; rect: SheetRect; size: SpriteSize };
  /** The PNG to upload (numbered frames are packed when asked). */
  sheet: () => Promise<Blob>;
}

export interface ReadExport {
  fileName: string;
  /** What the file holds, e.g. "4 sheets with JSON". */
  summary: string;
  rows: ImportRow[];
  /** PNGs with no readable grid: shown greyed, cut by hand from Media (035). */
  unknown: { name: string; path: string }[];
}

/** GIF and video exports are refused (D-2). */
const MOVING = /\.(gif|mp4|webm|mov|m4v|avi)$/i;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const png = (bytes: Uint8Array) => new Blob([bytes as BlobPart], { type: 'image/png' });
const totalMs = (cut: SheetCut) => frameDurations(cut).reduce((a, b) => a + b, 0);

function sizeOf(bytes: Uint8Array, path: string): SpriteSize {
  const size = pngSize(bytes);
  if (!size) throw new SheetImportError(`${path} is not a PNG`);
  checkSheetSize(size, path);
  return size;
}

function summarise(rows: ImportRow[], unknown: number): string {
  const json = rows.filter((r) => r.kind === 'json').length;
  const grid = rows.filter((r) => r.kind === 'grid').length;
  const frames = rows.filter((r) => r.kind === 'frames').length;
  const parts: string[] = [];
  if (json) parts.push(plural(json, 'sheet with JSON', 'sheets with JSON'));
  if (grid) parts.push(plural(grid, 'sheet with its grid in the name', 'sheets with their grid in the name'));
  if (frames) parts.push(plural(frames, 'folder of numbered frames', 'folders of numbered frames'));
  if (unknown) parts.push(plural(unknown, 'sheet with an unknown grid', 'sheets with an unknown grid'));
  return parts.join(', ');
}

/** A file's bytes (FileReader where `Blob.arrayBuffer` is missing). */
function readBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer().then((b) => new Uint8Array(b));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * The zip's files, each unpacked once and its bytes counted as they come out
 * of the decompressor (spec 034 AC-5). A zip's own size claims (central
 * directory, local headers) are never trusted: unpacking stops as soon as the
 * running total passes `IMPORT_MAX_BYTES`, so a zip that understates or
 * omits its sizes is refused before it fills memory. Only the entry count is
 * read from the listing, before anything is unpacked.
 */
export async function unpackZipEntries(zip: JSZip): Promise<{ path: string; size: number; bytes: Uint8Array }[]> {
  const files: { path: string; file: JSZip.JSZipObject }[] = [];
  zip.forEach((path, file) => { if (!file.dir) files.push({ path, file }); });
  checkExportLimits(files.map(() => ({ size: 0 })));
  const budget = { total: 0 };
  const out: { path: string; size: number; bytes: Uint8Array }[] = [];
  for (const { path, file } of files) {
    const bytes = await unpackMetered(file, budget);
    out.push({ path, size: bytes.length, bytes });
  }
  return out;
}

/** JSZip's documented `ZipObject#internalStream` (missing from its typings). */
interface ZipStream {
  on(event: 'data', cb: (chunk: Uint8Array) => void): ZipStream;
  on(event: 'error', cb: (err: unknown) => void): ZipStream;
  on(event: 'end', cb: () => void): ZipStream;
  resume(): ZipStream;
  pause(): ZipStream;
}

/** One entry's bytes, counted chunk by chunk against the shared budget. */
function unpackMetered(file: JSZip.JSZipObject, budget: { total: number }): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    let length = 0;
    let done = false;
    const stream = (file as unknown as { internalStream(type: 'uint8array'): ZipStream }).internalStream('uint8array');
    stream
      .on('data', (chunk) => {
        if (done) return;
        budget.total += chunk.length;
        if (budget.total > IMPORT_MAX_BYTES) {
          done = true;
          stream.pause();
          chunks.length = 0;
          reject(new SheetImportError(`The file unpacks to more than ${IMPORT_MAX_BYTES / (1024 * 1024)} MB; the limit is ${IMPORT_MAX_BYTES / (1024 * 1024)} MB`));
          return;
        }
        chunks.push(chunk);
        length += chunk.length;
      })
      .on('error', (err) => {
        if (done) return;
        done = true;
        reject(err instanceof SheetImportError ? err : new SheetImportError('This file is not a readable .zip'));
      })
      .on('end', () => {
        if (done) return;
        done = true;
        const bytes = new Uint8Array(length);
        let at = 0;
        for (const c of chunks) { bytes.set(c, at); at += c.length; }
        resolve(bytes);
      })
      .resume();
  });
}

/**
 * Read a dropped file into import rows, or throw a `SheetImportError` whose
 * message the dialog shows. A `.png` is read by its file name's grid; a
 * `.zip` is classified (`classifyExport`) after its limits are checked.
 */
export async function readSheetExport(file: File): Promise<ReadExport> {
  const name = file.name;
  if (MOVING.test(name)) throw new SheetImportError('Use a sprite sheet export instead');
  const lower = name.toLowerCase();

  let read: (path: string) => Promise<Uint8Array>;
  let entries: ExportEntry[];
  if (lower.endsWith('.png')) {
    entries = [{ path: name, size: file.size }];
    const bytes = await readBytes(file);
    read = async () => bytes;
  } else if (lower.endsWith('.zip')) {
    let zip: JSZip;
    try {
      zip = await JSZip.loadAsync(await readBytes(file));
    } catch {
      throw new SheetImportError('This file is not a readable .zip');
    }
    const listed = await unpackZipEntries(zip);
    checkExportLimits(listed);
    const byPath = new Map(listed.map((e) => [e.path, e.bytes]));
    read = async (path) => byPath.get(path)!;
    const text = new TextDecoder();
    entries = listed.map((e): ExportEntry => {
      if (!/\.json$/i.test(e.path) || e.path.startsWith('__MACOSX/')) return { path: e.path, size: e.size };
      try {
        return { path: e.path, size: e.size, json: JSON.parse(text.decode(e.bytes)) };
      } catch {
        return { path: e.path, size: e.size };
      }
    });
  } else {
    throw new SheetImportError('Choose a .zip or .png spritesheet export');
  }

  const contents = classifyExport(entries);
  const jsonOf = new Map(entries.map((e) => [e.path, e.json]));
  const rows: ImportRow[] = [];
  for (const a of contents.animations) {
    if (a.kind === 'frames') {
      const frames = await Promise.all(a.framePaths.map(read));
      const sizes = frames.map((bytes, i) => sizeOf(bytes, a.framePaths[i]));
      const layout = packLayout(sizes);
      const size = { width: layout.width, height: layout.height };
      checkSheetSize(size, `The packed sheet of ${a.name}`);
      const cut = packedCut(layout, frames.length);
      rows.push({
        id: a.framePaths[0], name: a.name, kind: 'frames', cut, size,
        firstFrame: sheetFrameRect(cut, 0, size),
        durationMs: totalMs(cut),
        still: { blob: png(frames[0]), rect: { x: 0, y: 0, w: sizes[0].width, h: sizes[0].height }, size: sizes[0] },
        sheet: async () => (await packFrames(frames.map(png))).blob,
      });
      continue;
    }
    const bytes = await read(a.sheetPath);
    const size = sizeOf(bytes, a.sheetPath);
    let cut: SheetCut;
    if (a.kind === 'json') {
      const parsed = parseSheetJson(jsonOf.get(a.jsonPath));
      checkMetaSize(parsed, size, a.sheetPath);
      const outside = parsed.frames.findIndex((r) => r.x + r.w > size.width || r.y + r.h > size.height);
      if (outside >= 0) throw new SheetImportError(`Frame ${outside + 1} of ${a.jsonPath} lies outside its ${size.width} × ${size.height} sheet`);
      cut = cutFromJson(parsed, a.sheetPath);
    } else {
      cut = cutFromGrid(a.grid);
    }
    const blob = png(bytes);
    const firstFrame = sheetFrameRect(cut, 0, size);
    rows.push({
      id: a.sheetPath, name: a.name, kind: a.kind, cut, size, firstFrame,
      durationMs: totalMs(cut),
      still: { blob, rect: firstFrame, size },
      sheet: async () => blob,
    });
  }
  if (rows.length === 0 && contents.unknown.length === 0) {
    throw new SheetImportError('Nothing in this file is a spritesheet export');
  }
  return { fileName: name, summary: summarise(rows, contents.unknown.length), rows, unknown: contents.unknown };
}

/** Where a row goes: an existing state of the sprite, or a new one. */
export type ImportTarget = { kind: 'fill'; state: string } | { kind: 'new'; name: string };

const reasonOf = (res: { success: boolean; error?: string } | null | undefined, fallback: string) =>
  (res && !res.success && res.error) || fallback;

const STATE_ERRORS: Record<SpriteStateError, string> = {
  empty: 'a new state needs a name',
  duplicate: 'a new state has the name of an existing one',
  last: 'a sprite keeps at least one state',
  missing: 'the sprite or one of its states no longer exists',
};

/** The sprite's top-level media folder (D-5), created when missing; `created` when this call made it. */
async function spriteFolder(client: Client, spriteName: string): Promise<{ id: number; created: boolean }> {
  const name = spriteName.trim() || 'Sprite';
  const list = await client.getMediaFolders();
  if (!list.success) throw new Error(reasonOf(list, 'the media folders could not be read'));
  const existing = list.data.find((f) => (f.parentId ?? null) === null && f.name === name);
  if (existing) return { id: existing.id, created: false };
  const made = await client.createMediaFolder({ name, parentId: null });
  if (!made.success) throw new Error(reasonOf(made, `the folder "${name}" could not be created`));
  return { id: made.data.id, created: true };
}

/**
 * Store the confirmed rows: upload each sheet into the sprite's media
 * folder, set its cut, then add and repoint the states in one undo step.
 * On any failure the media (and folder) this call created are deleted, the
 * scene is unchanged, and the error's message is the reason.
 */
export async function commitSheetImport(
  client: Client,
  spriteId: string,
  picks: { row: ImportRow; target: ImportTarget }[],
): Promise<void> {
  const sprite = useSceneStore.getState().scene?.sprites.find((s) => s.id === spriteId);
  if (!sprite) throw new Error(STATE_ERRORS.missing);
  const createdMedia: number[] = [];
  let folder: { id: number; created: boolean } | null = null;
  const items: (SheetImportItem & { cut: SheetCut })[] = [];
  try {
    folder = await spriteFolder(client, sprite.name);
    for (const { row, target } of picks) {
      const blob = await row.sheet();
      const file = new File([blob], `${row.name}.png`, { type: 'image/png' });
      const up = await client.uploadMedia(file, { name: row.name, folderId: folder.id });
      if (!up.success || !up.data?.id) throw new Error(reasonOf(up, `${row.name} could not be uploaded`));
      createdMedia.push(up.data.id);
      const patched = await client.updateMedia(up.data.id, { sheet: row.cut });
      if (!patched.success) throw new Error(reasonOf(patched, `the cut of ${row.name} could not be set`));
      items.push({
        stateName: target.kind === 'fill' ? target.state : target.name,
        create: target.kind === 'new',
        mediaId: up.data.id,
        naturalSize: row.size,
        cut: row.cut,
      });
    }
    // The frame-size rule (spec 033) reads the cut from the media sheet store.
    for (const item of items) useMediaSheetStore.getState().setSheet(item.mediaId, item.cut);
    const refused = useSceneStore.getState().importSheetStates(spriteId, items.map(({ cut: _cut, ...item }) => item));
    if (refused) throw new Error(STATE_ERRORS[refused]);
  } catch (err) {
    await Promise.all(createdMedia.map((id) => client.deleteMedia(id).catch(() => undefined)));
    if (folder?.created) await client.deleteMediaFolder(folder.id).catch(() => undefined);
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/**
 * Spec 035 D-5: one animation for a Pictures expression. Uploads the row's
 * sheet into the character's media folder (as 034 does for a sprite) and
 * sets its cut; resolves with the new media id. On failure what it created
 * is deleted and the error's message is the reason. The caller fills the
 * expression (`fillPicture`).
 */
export async function uploadSheetForPicture(client: Client, subject: string, row: ImportRow): Promise<{ mediaId: number; size: SpriteSize }> {
  let folder: { id: number; created: boolean } | null = null;
  let mediaId: number | null = null;
  try {
    folder = await spriteFolder(client, subject);
    const blob = await row.sheet();
    const file = new File([blob], `${row.name}.png`, { type: 'image/png' });
    const up = await client.uploadMedia(file, { name: row.name, folderId: folder.id });
    if (!up.success || !up.data?.id) throw new Error(reasonOf(up, `${row.name} could not be uploaded`));
    const id: number = up.data.id;
    mediaId = id;
    const patched = await client.updateMedia(id, { sheet: row.cut });
    if (!patched.success) throw new Error(reasonOf(patched, `the cut of ${row.name} could not be set`));
    useMediaSheetStore.getState().setSheet(id, row.cut);
    return { mediaId: id, size: row.size };
  } catch (err) {
    if (mediaId !== null) await client.deleteMedia(mediaId).catch(() => undefined);
    if (folder?.created) await client.deleteMediaFolder(folder.id).catch(() => undefined);
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/** `name`, or `name 2`, `name 3` … when it is taken. */
export function uniqueStateName(name: string, taken: Iterable<string>): string {
  const set = new Set([...taken].map((n) => n.trim()));
  if (!set.has(name)) return name;
  for (let i = 2; ; i++) if (!set.has(`${name} ${i}`)) return `${name} ${i}`;
}
